import type { ExecutedOrder } from '@dx-private/dxchart5-react/dist/chart/model/executed-orders.model'
import type {
  Order,
  OrderId,
  OrderWithId,
} from '@dx-private/dxchart5-react/dist/chart/model/trading/order.model'
import type { OrderProvider } from '@dx-private/dxchart5-react/dist/providers/trading/order.provider'
import type { TournamentWSClient, UpdateOperationParams } from '../sdk'
import {
  OperationStore,
  selectClosedPositionsBySymbol,
  selectOpenPositionsBySymbol,
  selectPendingOrdersBySymbol,
} from '../store'
import { tradeDisplayLog } from '../debug/browser-logging'
import {
  openPositionToEntryExecutedOrder,
  createProtectionOrderId,
  openOperationToSnapshot,
  pendingOperationToSnapshot,
  pendingOrderToDxOriginalOrder,
  snapshotToProtectionOrders,
} from './dx-trading-adapter'
import { blockReadonlyTradingMutation } from './readonly-trading'
import type { MergedSymbolInfo } from './dx-trading-mappers'
import { roundAndFormatPrice } from './symbol-price-precision'

// Task 2.1: Semantic projection/signature for DXChart order snapshots
// Used to detect when order content materially changes (as opposed to price-only ticks)
export interface OrderSnapshot {
  id: string
  originalItemId?: string
  type: 'original' | 'tp' | 'sl'
  orderType: string
  side: string
  quantity: number
  limitPrice?: number
  stopPrice?: number
  protectionOrderIds?: [OrderId | undefined, OrderId | undefined]
}

function createOrderSignature(orders: OrderSnapshot[]): string {
  // Deterministic signature including all semantic fields:
  // ID, type, original linkage, protection linkage, side, qty, order type, and prices
  const sorted = [...orders].sort((a, b) => a.id.localeCompare(b.id))
  return JSON.stringify(
    sorted.map((o) => ({
      id: o.id,
      type: o.type,
      originalItemId: o.originalItemId,
      protectionOrderIds: o.protectionOrderIds,
      orderType: o.orderType,
      side: o.side,
      quantity: o.quantity,
      limitPrice: o.limitPrice,
      stopPrice: o.stopPrice,
    }))
  )
}

interface OrderObserver {
  callback: (orders: OrderWithId[]) => void
  lastDeliveredSignature?: string
}

interface ExecutedOrderObserver {
  callback: (orders: ExecutedOrder[]) => void
  lastDeliveredSignature?: string
}

export class TournamentOrderProvider implements OrderProvider {
  private observerMap: Map<string, Set<OrderObserver>> = new Map()
  private executedObserverMap: Map<string, Set<ExecutedOrderObserver>> = new Map()
  private unsubscribeStoreFn?: () => void
  private selectedOperationId: string | null = null
  private hiddenByUser = false
  private lastLoggedOrderSignatureBySymbol = new Map<string, string>()
  private lastLoggedExecutedSignatureBySymbol = new Map<string, string>()

  private validateProtectionPrice(limitPrice: number | undefined, orderType: 'tp' | 'sl'): void {
    if (!Number.isFinite(limitPrice)) {
      const typeLabel = orderType === 'tp' ? 'take-profit' : 'stop-loss'
      throw new Error(
        `Invalid ${typeLabel} price: expected finite number, got ${typeof limitPrice === 'undefined' ? 'undefined' : limitPrice}`
      )
    }
  }

  // Task 3.1: Diagnostic helper for TP/SL mutation boundaries
  private protectionDiagnostic(stage: 'provider-invoked' | 'ws-request-sent' | 'ws-succeeded' | 'ws-rejected' | 'store-acked' | 'store-rejected' | 'emission-suppressed' | 'emission-delivered', operationId: string, protectionType: 'tp' | 'sl', details?: Record<string, unknown>): void {
    tradeDisplayLog(`protection-${protectionType}-${stage}`, {
      operationId,
      protectionType,
      stage,
      ...details,
    })
  }

  constructor(
    private store: OperationStore,
    private symbolFullNames: Record<string, string>,
    private wsClient: TournamentWSClient,
    private mergedSymbols?: Record<string, MergedSymbolInfo>
  ) {}

  observeOrders(symbol: string, dataCallback: (orders: OrderWithId[]) => void): () => void {
    const fullName = this.toFullSymbol(symbol)

    if (!this.observerMap.has(fullName)) {
      this.observerMap.set(fullName, new Set())
    }

    const observer: OrderObserver = { callback: dataCallback }
    this.observerMap.get(fullName)!.add(observer)

    if (!this.unsubscribeStoreFn) {
      this.attachStoreListener()
    }

    this.notifyOrders(fullName)

    return () => {
      const observers = this.observerMap.get(fullName)
      if (observers) {
        observers.delete(observer)
        // Task 2.4: Clean up observer signature state when last observer unsubscribes
        if (observers.size === 0) {
          this.observerMap.delete(fullName)
        }
      }

      if (this.observerMap.size === 0 && this.executedObserverMap.size === 0) {
        this.detachStoreListener()
      }
    }
  }

  observeExecutedOrders(
    symbol: string,
    dataCallback: (orders: ExecutedOrder[]) => void
  ): () => void {
    const fullName = this.toFullSymbol(symbol)

    if (!this.executedObserverMap.has(fullName)) {
      this.executedObserverMap.set(fullName, new Set())
    }

    const observer: ExecutedOrderObserver = { callback: dataCallback }
    this.executedObserverMap.get(fullName)!.add(observer)

    if (!this.unsubscribeStoreFn) {
      this.attachStoreListener()
    }

    this.notifyExecutedOrders(fullName)

    return () => {
      const observers = this.executedObserverMap.get(fullName)
      if (observers) {
        observers.delete(observer)
        // Task 2.4: Clean up observer signature state when last observer unsubscribes
        if (observers.size === 0) {
          this.executedObserverMap.delete(fullName)
        }
      }

      if (this.observerMap.size === 0 && this.executedObserverMap.size === 0) {
        this.detachStoreListener()
      }
    }
  }

  setVisibleSelection(selectedOperationId: string | null, hiddenByUser: boolean): void {
    const nextSelectedOperationId = selectedOperationId && selectedOperationId.length > 0
      ? selectedOperationId
      : null
    const nextHiddenByUser = Boolean(hiddenByUser)

    tradeDisplayLog('order-provider setVisibleSelection called', {
      previousSelectedOperationId: this.selectedOperationId,
      nextSelectedOperationId,
      previousHiddenByUser: this.hiddenByUser,
      nextHiddenByUser,
    })

    if (
      this.selectedOperationId === nextSelectedOperationId &&
      this.hiddenByUser === nextHiddenByUser
    ) {
      return
    }

    // Task 3.1: Emit selection-change diagnostic to track order selection lifecycle
    const selectionChanged = this.selectedOperationId !== nextSelectedOperationId
    const visibilityChanged = this.hiddenByUser !== nextHiddenByUser

    if (selectionChanged) {
      if (nextSelectedOperationId) {
        // Selection changed to a new operation
        tradeDisplayLog('protection-selection-changed', {
          operationId: nextSelectedOperationId,
          hiddenByUser: nextHiddenByUser,
        })
      } else {
        // Selection cleared
        tradeDisplayLog('protection-selection-cleared', {
          previousOperationId: this.selectedOperationId,
        })
      }
    } else if (visibilityChanged) {
      // Only visibility changed (same selection)
      tradeDisplayLog('protection-visibility-changed', {
        operationId: nextSelectedOperationId,
        hiddenByUser: nextHiddenByUser,
      })
    }

    this.selectedOperationId = nextSelectedOperationId
    this.hiddenByUser = nextHiddenByUser

    this.notifyAllOrderObservers()
  }

  async createOrder(symbol: string, order: Order): Promise<OrderId> {
    // See openspec/specs/tp-sl-drag-update/spec.md: protection create is the only createOrder carve-out.
    if (order.type === 'sl' || order.type === 'tp') {
      // Validation before any async operations
      this.validateProtectionPrice(order.limitPrice, order.type)
      const operationId = order.originalItemId
      const newPrice = order.limitPrice as number
      const shortName = this.toBackendSymbol(symbol)
      const merged = this.mergedSymbols?.[shortName]
      const roundedAndFormatted = roundAndFormatPrice(newPrice, merged?.symbolInfo)
      const updateParams: UpdateOperationParams = {
        id: operationId,
        ...(order.type === 'tp'
          ? { takeProfitPrice: roundedAndFormatted }
          : { stopLossPrice: roundedAndFormatted }),
      }

      // Task 3.1: Emit provider-invoked diagnostic
      this.protectionDiagnostic('provider-invoked', operationId, order.type, {
        newPrice: roundedAndFormatted,
        interaction: 'placement',
      })

      let response: Awaited<ReturnType<typeof this.wsClient.updateOperation>>
      try {
        // Task 3.1: Emit ws-request-sent diagnostic immediately before sending
        this.protectionDiagnostic('ws-request-sent', operationId, order.type)
        response = await this.wsClient.updateOperation(updateParams)

        // Task 3.1: Emit ws-succeeded diagnostic after successful response
        this.protectionDiagnostic('ws-succeeded', operationId, order.type)
      } catch (wsError) {
        // Task 3.1: Emit ws-rejected diagnostic only for WebSocket errors
        this.protectionDiagnostic('ws-rejected', operationId, order.type, {
          error: wsError instanceof Error ? wsError.message : String(wsError),
        })

        console.warn('[TournamentOrderProvider] createOrder failed for protection order:', {
          orderId: createProtectionOrderId(operationId, order.type),
          orderType: order.type,
          operationId,
          error: wsError,
        })
        throw wsError
      }

      // Dispatch after successful WebSocket response
      try {
        this.store.dispatch({ type: 'REQUEST_ACK_OPERATION_UPDATED', data: response })

        // Task 3.1: Emit store-acked diagnostic
        this.protectionDiagnostic('store-acked', operationId, order.type)
      } catch (storeError) {
        // Task 3.1: Emit store-rejected diagnostic if dispatch fails
        this.protectionDiagnostic('store-rejected', operationId, order.type, {
          error: storeError instanceof Error ? storeError.message : String(storeError),
        })
        throw storeError
      }

      return createProtectionOrderId(operationId, order.type) as OrderId
    }

    return blockReadonlyTradingMutation('create-order', '' as OrderId, {
      symbol: this.toBackendSymbol(symbol),
      orderType: order.type,
      orderSide: order.side,
      parentOperationId: order.type === 'original' ? undefined : order.originalItemId,
    })
  }

  async createOcoOrders(
    symbol: string,
    parentOrderId: OrderId,
    _orders: [Order | undefined, Order | undefined],
    _parentOrder?: OrderWithId
  ): Promise<[OrderId, OrderId]> {
    return blockReadonlyTradingMutation('create-oco-orders', ['' as OrderId, '' as OrderId], {
      symbol: this.toBackendSymbol(symbol),
      parentOrderId,
    })
  }

  async updateOrder(
    symbol: string,
    order: OrderWithId,
    _previousOrder?: OrderWithId
  ): Promise<void> {
    // Handle protection orders (TP/SL) - unblocked to support chart-side drag updates
    if (order.type === 'sl' || order.type === 'tp') {
      // Validation before any async operations
      this.validateProtectionPrice(order.limitPrice, order.type)
      const operationId = order.originalItemId
      const newPrice = order.limitPrice as number
      const shortName = this.toBackendSymbol(symbol)
      const merged = this.mergedSymbols?.[shortName]
      const roundedAndFormatted = roundAndFormatPrice(newPrice, merged?.symbolInfo)

      // Build the update parameters based on protection type
      const updateParams = {
        id: operationId,
        ...(order.type === 'tp' && { takeProfitPrice: roundedAndFormatted }),
        ...(order.type === 'sl' && { stopLossPrice: roundedAndFormatted }),
      }

      // Task 3.1: Emit provider-invoked diagnostic
      this.protectionDiagnostic('provider-invoked', operationId, order.type, {
        newPrice: roundedAndFormatted,
        interaction: 'drag',
      })

      let response: Awaited<ReturnType<typeof this.wsClient.updateOperation>>
      try {
        // Send the update to the backend via WS
        // Task 3.1: Emit ws-request-sent diagnostic immediately before sending
        this.protectionDiagnostic('ws-request-sent', operationId, order.type)
        response = await this.wsClient.updateOperation(updateParams)

        // Task 3.1: Emit ws-succeeded diagnostic after successful response
        this.protectionDiagnostic('ws-succeeded', operationId, order.type)
      } catch (wsError) {
        // Task 3.1: Emit ws-rejected diagnostic only for WebSocket errors
        this.protectionDiagnostic('ws-rejected', operationId, order.type, {
          error: wsError instanceof Error ? wsError.message : String(wsError),
        })

        // Log the error and ensure the promise rejects so DXChart can recover
        const errorMessage = wsError instanceof Error ? wsError.message : String(wsError)
        console.warn('[TournamentOrderProvider] updateOrder failed for protection order:', {
          orderId: order.id,
          orderType: order.type,
          error: errorMessage,
        })
        throw wsError
      }

      // Dispatch after successful WebSocket response
      try {
        // Dispatch local state update for immediate feedback
        this.store.dispatch({ type: 'REQUEST_ACK_OPERATION_UPDATED', data: response })

        // Task 3.1: Emit store-acked diagnostic
        this.protectionDiagnostic('store-acked', operationId, order.type)
      } catch (storeError) {
        // Task 3.1: Emit store-rejected diagnostic if dispatch fails
        this.protectionDiagnostic('store-rejected', operationId, order.type, {
          error: storeError instanceof Error ? storeError.message : String(storeError),
        })
        throw storeError
      }
      return
    }

    // All other order types remain blocked
    blockReadonlyTradingMutation('update-order', undefined, {
      symbol: this.toBackendSymbol(symbol),
      orderId: order.id,
      orderType: order.type,
      parentOperationId: order.type === 'original' ? order.id : order.originalItemId,
    })
  }

  async deleteOrder(symbol: string, order: OrderWithId): Promise<void> {
    blockReadonlyTradingMutation('delete-order', undefined, {
      symbol: this.toBackendSymbol(symbol),
      orderId: order.id,
      orderType: order.type,
      parentOperationId: order.type === 'original' ? order.id : order.originalItemId,
    })
  }

  private attachStoreListener(): void {
    this.unsubscribeStoreFn = this.store.subscribe(() => {
      Array.from(this.observerMap.keys()).forEach((symbol) => {
        this.notifyOrders(symbol)
      })

      Array.from(this.executedObserverMap.keys()).forEach((symbol) => {
        this.notifyExecutedOrders(symbol)
      })
    })
  }

  private detachStoreListener(): void {
    if (this.unsubscribeStoreFn) {
      this.unsubscribeStoreFn()
      this.unsubscribeStoreFn = undefined
    }
  }

  private notifyOrders(symbol: string): void {
    try {
      const { orders, snapshots, diagnostics } = this.resolveOrders(symbol)

      // Task 2.3: Suppress observer callbacks when semantic order snapshot unchanged
      const currentSignature = createOrderSignature(snapshots)

      const logPayload = orders.map((order) => ({
        id: order.id,
        type: order.type,
        orderType: order.orderType,
        originalItemId: 'originalItemId' in order ? order.originalItemId : undefined,
      }))
      const logSignature = JSON.stringify(logPayload)
      if (this.lastLoggedOrderSignatureBySymbol.get(symbol) !== logSignature) {
        this.lastLoggedOrderSignatureBySymbol.set(symbol, logSignature)
        tradeDisplayLog('order-provider emitted orders', {
          symbol,
          hiddenByUser: diagnostics.hiddenByUser,
          selectedOperationId: diagnostics.selectedOperationId,
          authoritativeSnapshots: diagnostics.authoritativeSnapshots,
          orders: logPayload,
        })
      }

      const observers = this.observerMap.get(symbol)
      if (observers) {
        Array.from(observers).forEach((obs) => {
          try {
            // Task 2.2: Deliver initial snapshot unconditionally; suppress if unchanged
            if (obs.lastDeliveredSignature === undefined || obs.lastDeliveredSignature !== currentSignature) {
              // Task 3.1: Emit emission-delivered diagnostic for protection orders
              if (snapshots.length > 0) {
                snapshots.forEach((snap) => {
                  if (snap.type === 'tp' || snap.type === 'sl') {
                    this.protectionDiagnostic('emission-delivered', snap.originalItemId || snap.id, snap.type)
                  }
                })
              }
              obs.callback(orders)
              // Task 2.2: Update signature only after successful delivery
              obs.lastDeliveredSignature = currentSignature
            } else {
              // Task 3.1: Emit emission-suppressed diagnostic for protection orders
              snapshots.forEach((snap) => {
                if (snap.type === 'tp' || snap.type === 'sl') {
                  this.protectionDiagnostic('emission-suppressed', snap.originalItemId || snap.id, snap.type)
                }
              })
            }
          } catch (error) {
            console.error('[TournamentOrderProvider] Observer callback failed for symbol:', symbol, error)
          }
        })
      }
    } catch (error) {
      console.error('Error notifying orders:', error)
    }
  }

  private notifyExecutedOrders(symbol: string): void {
    try {
      const { executedOrders, diagnostics } = this.resolveExecutedOrders(symbol)

      const logPayload = executedOrders.map((order) => ({
        id: order.id,
        side: order.side,
        price: order.price,
        timestamp: order.timestamp,
      }))
      const logSignature = JSON.stringify(logPayload)
      if (this.lastLoggedExecutedSignatureBySymbol.get(symbol) !== logSignature) {
        this.lastLoggedExecutedSignatureBySymbol.set(symbol, logSignature)
        tradeDisplayLog('order-provider emitted executed orders', {
          symbol,
          authoritativeOperationIds: diagnostics.authoritativeOperationIds,
          executedOrders: logPayload,
        })
      }

      const observers = this.executedObserverMap.get(symbol)
      if (observers) {
        // Execute position provider deduplication separately from order provider
        // Position P/L legitimately changes with market prices (design decision 3)
        Array.from(observers).forEach((obs) => {
          obs.callback(executedOrders)
        })
      }
    } catch (error) {
      console.error('Error notifying executed orders:', error)
    }
  }

  private getSnapshotsForSymbol(backendSymbol: string, state: ReturnType<typeof this.store.getState>) {
    const pending = selectPendingOrdersBySymbol(state, backendSymbol).map(pendingOperationToSnapshot)
    const open = selectOpenPositionsBySymbol(state, backendSymbol).map(openOperationToSnapshot)
    return [...pending, ...open]
  }

  getReadonlyDiagnostics(symbol: string): {
    orders: {
      backendSymbol: string
      hiddenByUser: boolean
      selectedOperationId: string | null
      authoritativeSnapshots: Array<{
        operationId: string
        state: 'pending' | 'open'
        hasStopLoss: boolean
        hasTakeProfit: boolean
      }>
      emittedOrderIds: string[]
    }
    executedOrders: {
      backendSymbol: string
      authoritativeOperationIds: string[]
      emittedExecutedOrderIds: string[]
    }
  } {
    const { orders, diagnostics: orderDiagnostics } = this.resolveOrders(symbol)
    const { executedOrders, diagnostics: executedDiagnostics } = this.resolveExecutedOrders(symbol)

    return {
      orders: {
        ...orderDiagnostics,
        emittedOrderIds: orders.map((order) => order.id),
      },
      executedOrders: {
        ...executedDiagnostics,
        emittedExecutedOrderIds: executedOrders.map((order) => order.id),
      },
    }
  }

  private notifyAllOrderObservers(): void {
    Array.from(this.observerMap.keys()).forEach((symbol) => {
      this.notifyOrders(symbol)
    })
  }

  private resolveOrders(symbol: string): {
    orders: OrderWithId[]
    snapshots: OrderSnapshot[]
    diagnostics: {
      backendSymbol: string
      hiddenByUser: boolean
      selectedOperationId: string | null
      authoritativeSnapshots: Array<{
        operationId: string
        state: 'pending' | 'open'
        hasStopLoss: boolean
        hasTakeProfit: boolean
      }>
    }
  } {
    const state = this.store.getState()
    const backendSymbol = this.toBackendSymbol(symbol)
    const hideAllOrders = this.hiddenByUser && this.selectedOperationId === null

    const pendingOrders = hideAllOrders
      ? []
      : selectPendingOrdersBySymbol(state, backendSymbol)
          .map(pendingOrderToDxOriginalOrder)
          .filter((order): order is OrderWithId => order !== null)
    const snapshots = hideAllOrders ? [] : this.getSnapshotsForSymbol(backendSymbol, state)

    // Task 2.1: Filter protection orders by selectedOperationId
    // Only emit TP/SL for the selected trade; if no trade is selected, emit zero protection pills
    const protectionOrders = snapshots
      .filter((snapshot) => snapshot.id === this.selectedOperationId)
      .flatMap(snapshotToProtectionOrders)

    // Task 2.1: Build semantic order snapshots for signature computation
    const orderSnapshots: OrderSnapshot[] = pendingOrders.map((o) => {
      const snap: OrderSnapshot = {
        id: o.id,
        type: o.type as 'original',
        orderType: o.orderType || '',
        side: o.side || '',
        quantity: o.quantity || 0,
        limitPrice: o.limitPrice,
        stopPrice: o.stopPrice,
      }
      if (o.type === 'original' && o.protectionOrderIds) {
        snap.protectionOrderIds = o.protectionOrderIds
      }
      return snap
    })

    protectionOrders.forEach((o) => {
      const snap: OrderSnapshot = {
        id: o.id,
        type: o.type as 'tp' | 'sl',
        orderType: o.orderType || '',
        side: o.side || '',
        quantity: o.quantity || 0,
        limitPrice: o.limitPrice,
        stopPrice: o.stopPrice,
      }
      if (o.type !== 'original') {
        snap.originalItemId = o.originalItemId
      }
      orderSnapshots.push(snap)
    })

    return {
      orders: [...pendingOrders, ...protectionOrders],
      snapshots: orderSnapshots,
      diagnostics: {
        backendSymbol,
        hiddenByUser: this.hiddenByUser,
        selectedOperationId: this.selectedOperationId,
        authoritativeSnapshots: snapshots.map((snapshot) => ({
          operationId: snapshot.id,
          state: snapshot.state,
          hasStopLoss: snapshot.stopLossPrice !== undefined,
          hasTakeProfit: snapshot.takeProfitPrice !== undefined,
        })),
      },
    }
  }

  private resolveExecutedOrders(symbol: string): {
    executedOrders: ExecutedOrder[]
    diagnostics: {
      backendSymbol: string
      authoritativeOperationIds: string[]
    }
  } {
    const state = this.store.getState()
    const backendSymbol = this.toBackendSymbol(symbol)
    const openOps = selectOpenPositionsBySymbol(state, backendSymbol)
    const closedOps = selectClosedPositionsBySymbol(state, backendSymbol)
    const replayTimeMs = state.currentReplayTime ? Date.parse(state.currentReplayTime) : NaN
    const replayDisplayTimeMs = state.currentReplayTimeDisplay
      ? Date.parse(state.currentReplayTimeDisplay)
      : NaN
    const timestampOffsetMs =
      Number.isFinite(replayTimeMs) && Number.isFinite(replayDisplayTimeMs)
        ? replayDisplayTimeMs - replayTimeMs
        : 0

    return {
      executedOrders: openOps.map((op) => openPositionToEntryExecutedOrder(op, timestampOffsetMs)),
      diagnostics: {
        backendSymbol,
        authoritativeOperationIds: [
          ...openOps.map((op) => op.order.id),
          ...closedOps.map((op) => op.order.id),
        ],
      },
    }
  }

  private toFullSymbol(symbol: string): string {
    return this.symbolFullNames[symbol] || symbol
  }

  private toBackendSymbol(symbol: string): string {
    if (this.symbolFullNames[symbol]) {
      return symbol
    }

    for (const [backendSymbol, fullSymbol] of Object.entries(this.symbolFullNames)) {
      if (fullSymbol === symbol) {
        return backendSymbol
      }
    }

    return symbol
  }
}
